import React, {
  useEffect,
  useState,
  useMemo,
} from 'react';
import PropTypes from 'prop-types';

import PanesWidget from '../PanesWidget/PanesWidget';
import Map from '../Map/Map';
import PibResidenceDetail from '../Residence/PibResidenceDetail';
import FloorPlanDetail from '../FloorPlan/FloorPlanDetail';
import PlaceDetail from '../Place/PlaceDetail';
import SubpaneMediaDetail from '../PanesWidget/SubpaneMediaDetail';
import ResourceDetail from '../MyResources/ResourcesDetail';
import { useConfigStore } from '../../stores';

/**
 * Helper to create resources pane data
 */
const createResourcesPaneData = (resourcesData) => [{
  id: '1',
  title: 'My Resources',
  ...resourcesData,
}];

const PibPageContent = ({
  communitySlug,
  mapFavoritesData,
  resourcesData,
  amenitiesServicesHTML,
  initialFavoriteData,
  favoritesData,
}) => {
  const [panesData, setPanesData] = useState(null);
  const featureFlags = useConfigStore((state) => state.featureFlags);
  const { platformShowSoldOutStatus } = featureFlags || {};

  // Use favoritesData from the optimized API endpoint (passed from parent)
  const residencesData = favoritesData?.residences || [];
  const floorPlansData = favoritesData?.floorPlans || [];
  const amenitiesData = favoritesData?.amenities || [];
  const mediaData = favoritesData?.media || [];

  // Memoize the sticky amenities blurb to prevent recreation on every render
  const FormattedStickyAmenitiesBlurb = useMemo(() => {
    if (!amenitiesServicesHTML) return null;

    return (
      <div className="pibPage-favorites__stickyContent">
        <div className="pibPage-favorites__stickyContent__title">Amenities & Services</div>
        <div className="pibPage-favorites__stickyContent__wysiwyg" dangerouslySetInnerHTML={{ __html: amenitiesServicesHTML }} />
      </div>
    );
  }, [amenitiesServicesHTML]);

  // Memoize components to prevent recreation on every render
  const memoizedMap = useMemo(() => (
    <div className="pibPage-map content-fadeIn">
      <div id="pibPage-mapModalRoot" className="modalRootClass" />
      <Map reset />
    </div>
  ), [mapFavoritesData]);

  // Memoize components to prevent recreation on every render
  const memoizedResidenceDetail = useMemo(() => (
    <PibResidenceDetail
      navClass="pibPage-favorites__detailNav"
      trackPibViews
    />
  ), []);

  const memoizedFloorPlanDetail = useMemo(() => (
    <FloorPlanDetail
      hasInternalHeader
      navClass="pibPage-favorites__detailNav"
      trackPibViews
    />
  ), []);

  const memoizedPlaceDetail = useMemo(() => (
    <PlaceDetail
      hasInternalHeader
      navClass="pibPage-favorites__detailNav"
      trackPibViews
    />
  ), []);

  const memoizedSubpaneMediaDetail = useMemo(() => (
    <SubpaneMediaDetail />
  ), []);

  const memoizedResourceDetail = useMemo(() => (
    <ResourceDetail />
  ), []);

  // Memoize each pane data separately for optimal performance
  const mapPaneData = useMemo(() => ({
    id: 'map',
    btnText: 'Map',
    paneData: [{ id: 'map', title: 'Map' }],
    subpaneComponent: memoizedMap,
    stickyMarkup: null,
  }), [memoizedMap]);

  const residencesPaneData = useMemo(() => {
    // Show pane only if we have residence data
    if (!residencesData || residencesData.length === 0) return null;

    // Transform data to format expected by PanesWidget
    const paneData = residencesData.map((residence) => ({
      id: residence.uuid,
      nid: residence.nid,
      title: residence.title,
      subpaneButtonText: residence.subpaneButtonText,
      coverImage: residence.coverImage,
      floorPlanUuid: residence.floorPlanUuid,
      floorPlanTitle: residence.floorPlanTitle,
    }));

    return {
      id: 'residences',
      btnText: 'My Residences',
      paneData,
      subpaneComponent: memoizedResidenceDetail,
      stickyMarkup: null,
    };
  }, [residencesData, memoizedResidenceDetail]);

  const floorPlansPaneData = useMemo(() => {
    if (!floorPlansData || floorPlansData.length === 0) return null;

    // Transform data to format expected by FloorPlanDetail
    const paneData = floorPlansData.map((floorPlan) => ({
      id: floorPlan.uuid,
      nid: floorPlan.nid,
      title: floorPlan.title,
      subpaneButtonText: floorPlan.subpaneButtonText,
      coverImage: floorPlan.coverImage,
      // twoD and threeD for FloorPlanImagesWidget
      twoD: floorPlan.twoD,
      threeD: floorPlan.threeD,
      bedBathSqft: floorPlan.bedBathSqft,
      description: floorPlan.description,
      photoGalleryUuid: floorPlan.photoGalleryUuid,
      photoGalleryImages: floorPlan.photoGalleryImages,
      videoUuid: floorPlan.videoUuid,
      // FloorPlanDetailCore expects video.videoId and video.service
      video: floorPlan.video ? {
        videoId: floorPlan.video.serviceVideoId,
        service: floorPlan.video.service,
      } : null,
      virtualTourUuid: floorPlan.virtualTourUuid,
      threeSixties: floorPlan.threeSixties,
      // Inventory data for sold out banners (only if feature flag enabled)
      remainingInventory: platformShowSoldOutStatus ? floorPlan.remainingInventory : null,
      soldOut: platformShowSoldOutStatus ? floorPlan.soldOut : null,
      inventoryPhrase: platformShowSoldOutStatus ? floorPlan.inventoryPhrase : null,
    }));

    return {
      id: 'floorPlans',
      btnText: 'My Floor Plans',
      paneData,
      subpaneComponent: memoizedFloorPlanDetail,
      stickyMarkup: null,
    };
  }, [floorPlansData, memoizedFloorPlanDetail, platformShowSoldOutStatus]);

  const amenitiesPaneData = useMemo(() => {
    if (!amenitiesData || amenitiesData.length === 0) return null;

    // Transform data to format expected by PanesWidget
    const paneData = amenitiesData.map((amenity) => ({
      id: amenity.uuid,
      nid: amenity.nid,
      title: amenity.title,
      subpaneButtonText: amenity.subpaneButtonText,
      coverImage: amenity.coverImage,
      category: amenity.category,
    }));

    return {
      id: 'amenities',
      btnText: 'My Amenities',
      paneData,
      subpaneComponent: memoizedPlaceDetail,
      stickyMarkup: FormattedStickyAmenitiesBlurb,
    };
  }, [amenitiesData, memoizedPlaceDetail, FormattedStickyAmenitiesBlurb]);

  const mediaPaneData = useMemo(() => {
    if (!mediaData || mediaData.length === 0) return null;

    // Transform data to format expected by SubpaneMediaDetail
    const paneData = mediaData.map((media) => ({
      id: media.uuid,
      nid: media.nid,
      title: media.title,
      subpaneButtonText: media.subpaneButtonText,
      coverImage: media.coverImage,
      type: media.type,
      // SubpaneMediaDetail expects video.videoId and video.service
      video: media.video ? {
        videoId: media.video.serviceVideoId,
        service: media.video.service,
      } : null,
      threeSixties: media.threeSixties,
      // SubpaneMediaDetail expects photoGalleryImages, not images
      photoGalleryImages: media.images,
    }));

    return {
      id: 'media',
      btnText: 'My Media',
      paneData,
      subpaneComponent: memoizedSubpaneMediaDetail,
      stickyMarkup: null,
    };
  }, [mediaData, memoizedSubpaneMediaDetail]);

  const resourcesPaneData = useMemo(() => {
    if (!resourcesData) return null;

    // Only show the pane if there are actual files or links
    const hasFiles = resourcesData.files && resourcesData.files.length > 0;
    const hasLinks = resourcesData.links && resourcesData.links.length > 0;
    if (!hasFiles && !hasLinks) return null;

    return {
      id: 'resources',
      btnText: 'My Resources',
      paneData: createResourcesPaneData(resourcesData),
      subpaneComponent: memoizedResourceDetail,
      stickyMarkup: null,
    };
  }, [resourcesData, memoizedResourceDetail]);

  // Combine all pane data into final array
  const panesDataMemo = useMemo(() => {
    // Wait for favorites data before creating panes
    if (!favoritesData) return null;

    const rawPanesData = [];

    // Add panes based on available data
    if (residencesPaneData) rawPanesData.push(residencesPaneData);
    if (floorPlansPaneData) rawPanesData.push(floorPlansPaneData);
    if (amenitiesPaneData) rawPanesData.push(amenitiesPaneData);
    if (mediaPaneData) rawPanesData.push(mediaPaneData);
    // Add resources pane last
    if (resourcesPaneData) rawPanesData.push(resourcesPaneData);

    // Always add map pane first - map should always be available
    rawPanesData.unshift(mapPaneData);

    return rawPanesData;
  }, [
    mapPaneData,
    residencesPaneData,
    floorPlansPaneData,
    amenitiesPaneData,
    mediaPaneData,
    resourcesPaneData,
    favoritesData,
  ]);

  // Update panesData when memoized data changes
  useEffect(() => {
    setPanesData(panesDataMemo);
  }, [panesDataMemo]);

  return (
    <div className="pibPage-favorites">
      <PanesWidget panesData={panesData} />
    </div>
  );
};

PibPageContent.propTypes = {
  communitySlug: PropTypes.string,
  mapFavoritesData: PropTypes.shape({
    residence: PropTypes.arrayOf(PropTypes.string),
    place: PropTypes.arrayOf(PropTypes.string),
  }),
  resourcesData: PropTypes.shape({
    links: PropTypes.array,
    files: PropTypes.array,
  }),
  amenitiesServicesHTML: PropTypes.string,
  initialFavoriteData: PropTypes.object,
  favoritesData: PropTypes.shape({
    residences: PropTypes.array,
    floorPlans: PropTypes.array,
    amenities: PropTypes.array,
    media: PropTypes.array,
  }),
};

PibPageContent.defaultProps = {
  communitySlug: null,
  mapFavoritesData: null,
  resourcesData: null,
  amenitiesServicesHTML: null,
  initialFavoriteData: null,
  favoritesData: null,
};

export default PibPageContent;
